/* Only Snacks disclosure - the open/close animation for every <details> that
   carries .os-disclosure (FAQ, PDP info panels, footer columns, the simple
   product page). Native <details> snaps open with no transition, so height is
   animated with the Web Animations API and the content cross-fades. Honours
   prefers-reduced-motion.

   Heights are MEASURED from the real layout (padding, margins and borders
   included) rather than summed from child offsets, so the panel lands exactly
   on its natural size and never jumps at the end of the animation.

   Markup contract:
     <details class="os-disclosure">
       <summary>…</summary>
       <div class="os-disclosure__content">…</div>
     </details>
   Wrap a group in [data-os-accordion] and only one panel stays open at a time
   (opening one closes the others with the same animation).
*/
(() => {
  // Timing lives in the stylesheet next to the chevron transition
  // (os-primitives.css sets --os-disclosure-duration on .os-disclosure), so
  // the two motions are tuned in one place; the fallback covers a stray
  // <details> without the primitive's rule.
  const FALLBACK_MS = 280;
  const EASING = 'cubic-bezier(.2,.8,.3,1)';
  const durationOf = (el) => {
    const raw = getComputedStyle(el).getPropertyValue('--os-disclosure-duration').trim();
    const n = parseFloat(raw);
    if (!n) return FALLBACK_MS;
    return raw.endsWith('ms') ? n : n * 1000;
  };
  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const instances = new WeakMap();

  class OSDisclosure {
    constructor(el) {
      this.el = el;
      this.summary = el.querySelector('summary');
      this.content = el.querySelector('.os-disclosure__content');
      if (!this.summary || !this.content) return;
      this.animation = null;
      this.closing = false;
      this.summary.addEventListener('click', (e) => this.onClick(e));
      el.dataset.osDisclosureReady = 'true';
      instances.set(el, this);
    }

    // Close every other panel in the same [data-os-accordion] group.
    closeSiblings() {
      const group = this.el.closest('[data-os-accordion]');
      if (!group) return;
      group.querySelectorAll('details.os-disclosure').forEach((other) => {
        if (other === this.el) return;
        const inst = instances.get(other);
        const opening = inst?.animation ? !inst.closing : other.open;
        if (!opening) return;
        if (inst && !reduced()) inst.shrink(); else other.open = false;
      });
    }

    onClick(e) {
      const opening = this.animation ? this.closing : !this.el.open;
      if (opening) this.closeSiblings();
      if (reduced()) return;            // let the browser toggle instantly
      e.preventDefault();
      if (opening) this.expand(); else this.shrink();
    }

    // Natural height of the panel in a given state, measured without the
    // inline height that a running animation may have left behind.
    measure(open) {
      const { el, content } = this;
      const prevHeight = el.style.height;
      const prevDisplay = content.style.display;
      const wasOpen = el.open;
      el.style.height = '';
      el.open = true;
      content.style.display = open ? '' : 'none';
      const h = el.getBoundingClientRect().height;
      content.style.display = prevDisplay;
      el.open = wasOpen;
      el.style.height = prevHeight;
      return h;
    }

    run(start, end, closing) {
      const { el, content } = this;
      const duration = durationOf(el);
      this.animation?.cancel();
      el.style.overflow = 'hidden';
      el.style.height = `${start}px`;
      el.open = true;                   // content must be in the DOM to show/hide it
      // Force a layout so the height animation starts from `start`, not from
      // whatever the previous frame had (no rAF: it stalls in background tabs).
      void el.offsetHeight;
      const opts = { duration, easing: EASING };
      const a = el.animate({ height: [`${start}px`, `${end}px`] }, opts);
      // Closing: fade out over the first 70% and HOLD at 0 (fill forwards) until the
      // panel has actually closed - otherwise the text pops back for the last frames.
      this.fade?.cancel();
      this.fade = content.animate(
        { opacity: closing ? [1, 0] : [0, 1] },
        {
          ...opts,
          duration: duration * 0.7,
          delay: closing ? 0 : duration * 0.3,
          fill: closing ? 'forwards' : 'backwards',
        },
      );
      this.closing = closing; // direction of the running animation
      a.onfinish = () => this.finish(!closing);
      a.oncancel = () => { if (this.animation === a) this.animation = null; };
      this.animation = a;
    }

    expand() {
      const start = this.el.getBoundingClientRect().height;
      this.run(start, this.measure(true), false);
    }

    shrink() {
      const start = this.el.getBoundingClientRect().height;
      this.run(start, this.measure(false), true);
    }

    finish(open) {
      this.el.open = open;
      this.animation = null;
      this.el.style.height = '';
      this.el.style.overflow = '';
      // Release the held opacity only once the panel is closed/open.
      this.fade?.cancel();
      this.fade = null;
    }
  }

  const init = (root = document) =>
    root.querySelectorAll('details.os-disclosure:not([data-os-disclosure-ready])')
        .forEach((el) => new OSDisclosure(el));

  document.addEventListener('DOMContentLoaded', () => init());
  init();
  // Theme editor re-renders sections without a page load.
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
